"use client";

import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useState,
} from "react";

/**
 * Wrapper around a wizard's step sections that animates the height lock from
 * {@link useWizardHeightLock}. The outer div carries `lockStyle` (exact
 * height) and a smooth height transition; the inner `stepsRef` div is
 * measured at its natural height (unaffected by the lock), so the grow/shrink
 * measurement doesn't get feedback from the animated height.
 *
 * Overflow is clipped ONLY in a short window after each step change (while
 * the height animates), so tall content reveals progressively with the card
 * instead of popping in at full height. Outside that window overflow is
 * visible, so inline popovers (e.g. address autocomplete, positioned
 * `absolute` below the field) don't get clipped. `motion-reduce` disables the
 * transition - height is then set instantly, still without a jump.
 *
 * `direction` drives an enter-only fade+slide of the active step: +1
 * (forward) slides in from the right, -1 (back) from the left, so the
 * movement direction reads correctly. The transform doesn't affect the
 * layout box, so height measurement is unaffected; the brief offset gets
 * clipped by the overflow window above. Only enter is animated (the outgoing
 * step unmounts) — double-mounting would interfere with height measurement.
 */
export function WizardStepShell({
  stepKey,
  direction = 1,
  stepsRef,
  lockStyle,
  children,
}: {
  stepKey: number | string;
  direction?: 1 | -1;
  stepsRef: RefObject<HTMLDivElement | null>;
  lockStyle: CSSProperties | undefined;
  children: ReactNode;
}) {
  const [clip, setClip] = useState(true);

  // stepKey is the trigger, not a value we read: each step change re-clips
  // through the height animation, then opens back up for inline popovers.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepKey drives the reset
  useLayoutEffect(() => {
    setClip(true);
    const id = setTimeout(() => setClip(false), 260);
    return () => clearTimeout(id);
  }, [stepKey]);

  const enter = direction >= 0 ? "slide-in-from-right-3" : "slide-in-from-left-3";

  return (
    <div
      className={`transition-[height] duration-200 ease-out motion-reduce:transition-none ${
        clip ? "overflow-hidden" : ""
      }`}
      style={lockStyle}
    >
      <div ref={stepsRef}>
        <div
          key={stepKey}
          className={`animate-in fade-in ${enter} duration-200 ease-out motion-reduce:animate-none`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
